import React from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import onClickOutside from 'react-onclickoutside'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faChevronDown, faCheck } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import '../assets/css/components/tyto-select.css'
library.add(faChevronDown, faCheck)

// NOTE: Please DO NOT use this component anymore, and use React-select lib instead.
// This custom component was creted when React-select lib did not support 
// multi select. React-select has since then updated to support all of the 
// different types of custom inputs this component supports, making this component
// obsolete. We will slowly rotate out of this component, and eventually remove from
// code base.

/**
 * TytoSelect is a custom select speced according to Arielle's designs.
 * It can act as a standard select, multi-select, and searchable select.
 */
class TytoSelect extends React.Component {
  static propTypes = {
    /** The IDs that are selected by the user. For single selects, use a number. For multiple, use an array. */
    selections:   PropTypes.oneOfType([ PropTypes.arrayOf(PropTypes.number), PropTypes.number, PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    /** The options to be displayed and selected from. The option values can be text, thus should be checked as either string or number*/
    options:      PropTypes.arrayOf(PropTypes.exact({ value: PropTypes.oneOfType([PropTypes.string,PropTypes.number]), label: PropTypes.string })),
    /** For single inputs, this is used instead of placeholder text to reflect the label of the
     * currently-selected item. This is ignored for multiple selects. */
    placeholderTextUsesSelections: PropTypes.bool,
    /** The function to be executed upon a selection (single or multiple) being made. Usually should be
     * a redux action dispatch. */
    onSelect:     PropTypes.func.isRequired,
    /** Static text that will appear on the select input. */
    placeholder:  PropTypes.string,
    /** Enables multiple options to be selected by the user instead of just one. */
    multiple:     PropTypes.bool,
    /** Enables a search text field that can filter the options by label. Works for both single and multiple inputs. */
    searchable:   PropTypes.bool,
    /** Text to display when a searchable select is being searched and returns zero results. */
    emptySearchText:  PropTypes.string
  }

  static defaultProps = {
    selections:                     [],
    placeholderTextUsesSelections:  false,
    multiple:                       false,
    searchable:                     false
  }

  constructor(props) {
    super(props)
    this.state = { isExpanded: false, searchValue: '' }
    this.searchInput = React.createRef()
  }

  handleKeyDown = evt => {
    if(this.state.isExpanded && this.searchInput.current){
      // Ideally, setTimeout should not be needed, but without the setTimeout there is a
      // 1-character delay when setting state
      setTimeout(()=>this.setState({ searchValue: this.searchInput.current.value }), 10)
    }
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown)
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown)
  }

  componentDidUpdate() {
    if(this.state.isExpanded && this.searchInput.current){
      // Constantly focus the input
      this.searchInput.current.focus()
    }
  }

  handleExpansionToggle = ()=>
    this.setState({ isExpanded: !this.state.isExpanded, searchValue: '' })

  handleClickOutside = evt =>
    this.setState({ isExpanded: false })

  getActionComponent = ()=> {
    if(this.props.multiple && this.state.isExpanded) {
      return(
        <span
          onClick={this.handleExpansionToggle}
          className='text-uppercase commit'>Done</span>
      )
    } else {
      return <FontAwesomeIcon icon='chevron-down'/>
    }
  }

  handleOptionSelect = val => {
    const { onSelect, multiple } = this.props
    onSelect(val)
    if(!multiple){ this.handleExpansionToggle() }
  }

  getFieldClasses = ()=>{
    const baseClasses = 'field'
    const expandedClass = this.state.isExpanded ? 'expanded' : ''

    return [baseClasses, expandedClass].join(' ')
  }

  getPreviewText = ()=>{
    const { placeholderTextUsesSelection, options, placeholder } = this.props

    if(placeholderTextUsesSelection) {
      return (_.find(options, option => option.value === this.props.selections) || {}).label || placeholder
    } else {
      return placeholder
    }
  }

  handleFieldClick = ()=>{
    if(!this.state.isExpanded){ this.handleExpansionToggle() }
  }

  getOptionClasses = option => {
    const baseClasses = 'option'
    const foundOption = _.find(this.props.selections, sel => sel.toString() === option.value.toString())

    // String check is for ids like "1"
    if(_.isNumber(foundOption) || _.isString(foundOption)) {
      return [baseClasses, 'selected'].join(' ')
    } else {
      return baseClasses
    }
  }

  getVisibleOptions = ()=>{
    if(!this.props.searchable || _.isEmpty(this.state.searchValue)){
      return this.props.options
    } else {
      let match
      let textToBold
      // Certain characters like [, ], *, etc can break the regexp check below. The following solution
      // is fairly restrictive in that is only allows letters, numbers, underscores, and dashes. This
      // restriction may need to be expanded eventually.
      const cleanedValue = this.state.searchValue.replace(/[^a-zA-Z0-9_-\s]/g, '').toLowerCase()
      const searchRegExp = new RegExp(cleanedValue, 'i')

      const filteredOptions = _.map(this.props.options, op => {
        match = op.label.match(searchRegExp)
        if(match) {
          textToBold = match[0]
          return { value: op.value, label: op.label.replace(textToBold, "<b>"+textToBold+"</b>") }
        }
      })

      return _.compact(filteredOptions)
    }
  }

  render() {
    return(
      <div className='tyto-select'>
        <div onClick={this.handleFieldClick} className={this.getFieldClasses()}>
          {this.state.isExpanded && this.props.searchable &&
            <input ref={this.searchInput} placeholder={this.getPreviewText()}></input>
          }
          <span className='selection-text'>{this.getPreviewText()}</span>
          <span className='right-action'>
            {this.getActionComponent()}
          </span>
        </div>
        {this.state.isExpanded &&
          <div className='selection-container'>
            {_.map(this.getVisibleOptions(), (option,index) =>
              <div
                key={index}
                onClick={()=>this.handleOptionSelect(option.value)}
                className={this.getOptionClasses(option)}>
                {this.props.multiple && <span className='checkbox'><FontAwesomeIcon icon='check'/></span>}
                <span dangerouslySetInnerHTML={{__html: option.label}} className='text'></span>
              </div>
            )}
            {this.getVisibleOptions().length == 0 &&
              <div className='empty-search'>Sorry, no results found!</div>
            }
          </div>
        }
      </div>
    );
  }
}

export default onClickOutside(TytoSelect)
